import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';
import { googleAuthService } from '../../services/googleAuth';
import { useApp } from '../../contexts/AppContext';
import { GoogleIcon } from '../../icons';
import LoadingSpinner from '../ui/LoadingSpinner';

interface GoogleSignInButtonProps {
  mode: 'signin' | 'signup';
  onError: (error: string) => void;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  mode,
  onError,
}) => {
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleCallback = async (credential: string) => {
    try {
      const response = await authService.loginWithGoogle({
        id_token: credential,
      });
      dispatch({ type: 'SET_USER', payload: response.user });
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Google auth error:', err);
      const errorMessage =
        err.response?.data?.error || `Google ${mode} failed. Please try again.`;
      onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    onError(''); // Clear any existing errors

    try {
      const credential = await googleAuthService.signIn();
      await handleGoogleCallback(credential);
    } catch (error) {
      console.error('Google auth error:', error);
      onError(`Google ${mode} failed. Please try again.`);
      setIsLoading(false);
    }
  };

  const buttonText =
    mode === 'signin' ? 'Sign in with Google' : 'Sign up with Google';
  const loadingText =
    mode === 'signin'
      ? 'Signing in with Google...'
      : 'Signing up with Google...';

  return (
    <div className="mt-6">
      {isLoading ? (
        <div className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400">
          <LoadingSpinner size="sm" />
          <span className="ml-2">{loadingText}</span>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <GoogleIcon size={20} />
          <span className="ml-2">{buttonText}</span>
        </button>
      )}
    </div>
  );
};

export default GoogleSignInButton;
